import React, { useState } from 'react';
import { ShieldCheck, Check, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { ApprovalTracker, approvalLevelLabel } from './ApprovalTracker';
import type { Approval, ApprovalLevel, PayrollRun, RunState } from '../types';

const ACTION_LABEL: Record<ApprovalLevel, string> = { hr: 'HR', finance: 'Finance', group: 'Group Finance', final: 'Final' };

/** Which approval policy the run was submitted under, in words. */
export function submittedUnderText(
  run: Pick<PayrollRun, 'policySource' | 'policyVersion'> | undefined,
  runState: RunState,
): string | null {
  if (!run || runState === 'draft') return null;
  const v = run.policyVersion ? ` (version ${run.policyVersion})` : '';
  switch (run.policySource) {
    case 'entity':
      return `Submitted under this company's own approval policy${v}.`;
    case 'tenant':
      return `Submitted under the company-group default approval policy${v}.`;
    case 'builtin':
      return 'Submitted under the built-in approval chain (no policy was configured).';
    default:
      // No snapshot on the run: it was submitted before policies were recorded on runs,
      // so it follows the company's CURRENT policy. Said plainly rather than left blank.
      return "This run has no recorded policy snapshot, so it follows the company's current approval policy.";
  }
}

interface ApprovalsTabProps {
  approvals: Approval[];
  runState: RunState;
  busy: boolean;
  onApprove: (level: ApprovalLevel) => void;
  onReject: (note: string) => void;
  /** The run, for the policy it was submitted under (policySource/policyVersion). */
  run?: Pick<PayrollRun, 'policySource' | 'policyVersion'>;
}

export const ApprovalsTab: React.FC<ApprovalsTabProps> = ({ approvals, runState, busy, onApprove, onReject, run }) => {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  // The actionable level is the first one that is NOT APPROVED — the same rule the
  // backend applies in NextPendingLevelIn. Matching `decision === 'pending'` instead
  // was a dead end: a run rejected at a level and then validated again carries a
  // stale `rejected` row, so NO level read as 'pending', `canDecide` went false, and
  // both buttons below disappeared — leaving a run that could neither be approved
  // nor sent back to draft (Reject is the only route from validated to draft).
  const nextLevel = approvals.find((a) => a.decision !== 'approved');
  const canDecide = runState === 'validated' && nextLevel;
  const reworked = nextLevel?.decision === 'rejected';
  const complete = approvals.every((a) => a.decision === 'approved');
  const hasGroupStage = approvals.some((a) => a.scope === 'group');
  const policyLine = submittedUnderText(run, runState);

  return (
    <div className="grid gap-8 md:grid-cols-[1fr_320px]">
      <div>
        <h3 className="mb-4 text-sm font-semibold text-charcoal">Approval chain</h3>
        <ApprovalTracker approvals={approvals} />
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-charcoal">
            <ShieldCheck className="h-4 w-4 text-[#D9711E]" />
            Segregation of duties
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            HR and Finance must be signed off by different people. The final sign-off may be completed
            by the payroll preparer once Finance has approved. A run must clear HR, Finance, then Final
            before it can be locked and paid.
          </p>
          {hasGroupStage && (
            <p className="mt-2 text-xs text-muted-foreground">
              Stages marked <strong>Group Finance</strong> can only be signed by someone with access to every company.
            </p>
          )}
          {policyLine && <p className="mt-2 text-xs text-muted-foreground" data-testid="submitted-under">{policyLine}</p>}
        </div>

        {canDecide && !rejecting && (
          <div className="space-y-2">
            {reworked && (
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                This level was rejected and the run has since been re-validated. Approving
                records a fresh decision against the current figures.
              </p>
            )}
            <Button className="w-full gap-2" disabled={busy} onClick={() => onApprove(nextLevel!.level)}>
              <Check className="h-4 w-4" />
              Approve as {ACTION_LABEL[nextLevel!.level] ?? approvalLevelLabel(nextLevel!)}
            </Button>
            <Button variant="outline" className="w-full gap-2" disabled={busy} onClick={() => setRejecting(true)}>
              <X className="h-4 w-4" />
              Reject &amp; reopen
            </Button>
          </div>
        )}

        {canDecide && rejecting && (
          <div className="space-y-2">
            <Textarea
              placeholder="Reason for rejection (returns the run to draft)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="destructive"
                className="flex-1"
                disabled={busy || !note.trim()}
                onClick={() => { onReject(note.trim()); setRejecting(false); setNote(''); }}
              >
                Confirm reject
              </Button>
              <Button variant="outline" onClick={() => setRejecting(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {runState === 'draft' && (
          <p className="text-sm text-muted-foreground">Validate the run before approvals can begin.</p>
        )}
        {complete && runState !== 'validated' && (
          <div className="rounded-xl border border-[#166534]/20 bg-[#DCFCE7]/40 p-4 text-sm text-[#166534]">
            All approvals complete — the run is cleared for locking.
          </div>
        )}
      </div>
    </div>
  );
};
